import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  FormControl,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../../shared/services/project/project.service';
import { VersionService } from '../../../shared/services/version/version.service';
import { MessageService } from '../../../shared/services/message/message.service';
import { ToastrService } from 'ngx-toastr';
import { RELOAD_VERSIONS } from '../../../shared/constants/general.constants';
import { FullProject, ProjectVersion } from '@app/shared/interfaces';
import { AppStateService } from '../../../shared/services/app-state/app-state.service';
import { LocalstorageHelper } from '../../../shared/helpers/localstorage.helper';
import { NotebookDefaultFilesService } from '../../../shared/services/notebook-default-file/notebook-default-file.service';
import { CacheService } from '../../../shared/services/cache/cache.service';
import { firstValueFrom } from 'rxjs';

export interface VersionDialogData {
  projectId: number;
}

@Component({
  selector: 'app-version-dialog',
  templateUrl: './version-dialog.component.html',
  styleUrls: ['./version-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
})
export class VersionDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private readonly projectService = inject(ProjectService);
  private versionService = inject(VersionService);
  private messageService = inject(MessageService);
  private toastr = inject(ToastrService);
  private appStateService = inject(AppStateService);
  private readonly notebookDefaultFilesService = inject(NotebookDefaultFilesService);
  private cacheService = inject(CacheService);
  dialogRef = inject<MatDialogRef<VersionDialogComponent>>(MatDialogRef);
  data = inject<VersionDialogData>(MAT_DIALOG_DATA);

  versionForm: FormGroup;
  isLoading = signal(false);
  latestVersion = signal('2.9'); // Default value, will be updated from service

  private readonly MAX_VERSION_NUMBER: number = 2147483647;
  // Max length for the optional version message; bound in the template too.
  readonly MESSAGE_MAX_LENGTH = 500;
  private projectData: FullProject | null = null;
  private isCreatingVersion = false; // Flag to prevent duplicate creation attempts

  constructor() {
    this.versionForm = this.fb.group({
      majorVersion: [
        null,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(this.MAX_VERSION_NUMBER),
          Validators.pattern('^[0-9]*$'),
        ],
      ],
      minorVersion: [
        null,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(this.MAX_VERSION_NUMBER),
          Validators.pattern('^[0-9]*$'),
        ],
      ],
      // Optional human-readable message describing the version (SCRUM-777).
      message: [null, [Validators.maxLength(this.MESSAGE_MAX_LENGTH)]],
    });

    // Add the custom validator after form creation
    this.versionForm.setValidators(this.validateVersion());
  }

  ngOnInit(): void {
    this.loadLatestVersion();
  }

  // Custom validator to ensure majorVersion > 0 when minorVersion is 0 (prevents 0.0 versions)
  private validateVersion() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const majorVersion = formGroup.get('majorVersion')?.value;
      const minorVersion = formGroup.get('minorVersion')?.value;

      if (Number(majorVersion) === 0 && Number(minorVersion) === 0) {
        formGroup.get('minorVersion')?.setErrors({ min: true });
        return { min: true };
      }

      return null;
    };
  }

  private loadLatestVersion(): void {
    // Get the project with its versions
    this.projectService.getProject(this.data.projectId).subscribe({
      next: (project: FullProject) => {
        this.projectData = project;

        if (project.versions && project.versions.length > 0) {
          // Find the latest version (sort by major.minor descending)
          const sortedVersions = project.versions.sort(
            (a: ProjectVersion, b: ProjectVersion) => {
              if (a.majorVersion !== b.majorVersion) {
                return b.majorVersion - a.majorVersion;
              }
              return b.minorVersion - a.minorVersion;
            },
          );

          const latestVersionObj = sortedVersions[0];
          this.latestVersion.set(`${latestVersionObj.majorVersion}.${latestVersionObj.minorVersion}`);

          // Pre-fill suggested next version (increment minor version)
          this.versionForm.patchValue({
            majorVersion: latestVersionObj.majorVersion,
            minorVersion: latestVersionObj.minorVersion + 1,
          });
        } else {
          // If no versions exist, suggest starting with 1.0
          this.latestVersion.set('N/A');
          this.versionForm.patchValue({
            majorVersion: 1,
            minorVersion: 0,
          });
        }
      },
      error: (error: any) => {
        console.error('Error loading project versions:', error);
        // Use default values if unable to load
        this.versionForm.patchValue({
          majorVersion: 3,
          minorVersion: 0,
        });
      },
    });
  }

  private checkForDuplicateVersion(
    majorVersion: number,
    minorVersion: number,
  ): boolean {
    if (!this.projectData?.versions) {
      return false;
    }

    return this.projectData.versions.some(
      (version: ProjectVersion) =>
        version.majorVersion === majorVersion &&
        version.minorVersion === minorVersion,
    );
  }

  onSubmit(): void {
    if (this.versionForm.invalid || this.isLoading()) {
      return;
    }

    const { majorVersion, minorVersion } = this.versionForm.value;
    const majorVersionNum = Number(majorVersion);
    const minorVersionNum = Number(minorVersion);

    // Refresh project data before checking for duplicates to ensure we have the latest versions
    this.projectService.getProjectFresh(this.data.projectId).subscribe({
      next: (project: FullProject) => {
        this.projectData = project;

        // Check for duplicate versions with fresh data
        if (this.checkForDuplicateVersion(majorVersionNum, minorVersionNum)) {
          this.toastr.error(
            `Version ${majorVersionNum}.${minorVersionNum} already exists`,
            'Error',
          );
          return;
        }

        // If no duplicate, proceed with version creation
        this.createVersion(majorVersionNum, minorVersionNum);
      },
      error: (error: any) => {
        console.error('Error loading project versions:', error);
        // Continue with duplicate check using cached data as fallback
        if (this.checkForDuplicateVersion(majorVersionNum, minorVersionNum)) {
          this.toastr.error(
            `Version ${majorVersionNum}.${minorVersionNum} already exists`,
            'Error',
          );
          return;
        }
        // Proceed with creation if no duplicate found
        this.createVersion(majorVersionNum, minorVersionNum);
      },
    });
  }

  private async createVersion(
    majorVersionNum: number,
    minorVersionNum: number,
  ): Promise<void> {
    // Guard: Prevent duplicate creation attempts
    if (this.isCreatingVersion) {
      console.warn('Version creation already in progress');
      return;
    }

    // Guard: Check project lock
    if (!this.appStateService.isCurrentEditor()) {
      this.toastr.error(
        'You must have the project lock to create a version',
        'Error',
      );
      return;
    }

    this.isCreatingVersion = true;
    this.isLoading.set(true);

    let versionCreated = false;
    let response: any;
    let updatedProject: FullProject | undefined;
    let hadError = false;

    try {
      // ========================================
      // SIMPLIFIED: Backend copies files automatically
      // Frontend only orchestrates the flow
      // ========================================

      // PHASE 1: Create version via backend API
      // Backend automatically:
      //   - Freezes current HEAD with major/minor numbers
      //   - Creates new HEAD (0.0)
      //   - Database trigger copies file records
      //   - App server copies file content to storage
      response = await this.callBackendCreateVersion(
        majorVersionNum,
        minorVersionNum,
      );
      versionCreated = true; // Mark as created for success handling
      console.log(
        '✅ Version created successfully, versionCreated flag set to true',
      );

      // PHASE 2: Fetch updated project data
      updatedProject = await this.fetchUpdatedProjectData();

      // PHASE 3: Clear caches and refresh AppState
      // clearFullVersionCache() ensures GET returns fresh data
      await this.clearCachesAndRefreshState();

      // PHASE 4: Notify project-header via message bus
      // Updates dropdown with new versions
      this.notifyProjectHeaderOfNewVersion(
        updatedProject,
        response,
        majorVersionNum,
        minorVersionNum,
      );

      // PHASE 5: Navigate to created frozen version
      await this.navigateToCreatedVersion(
        updatedProject,
        majorVersionNum,
        minorVersionNum,
      );

      // Show success toast only if we got here without errors
      this.toastr.success(
        `Version v${majorVersionNum}.${minorVersionNum} created`,
        'Success',
      );
    } catch (error: any) {
      hadError = true;
      console.error('❌ Error in createVersion:', error);
      console.log('Version created flag:', versionCreated);

      // If version was created but post-processing failed, still close dialog
      if (versionCreated) {
        console.warn('Version created but post-processing had errors:', error);
        this.toastr.warning(
          `Version v${majorVersionNum}.${minorVersionNum} created but some updates may require a page refresh`,
          'Partial Success',
        );
      } else {
        // Version creation itself failed
        this.handleCreationError(error);
      }
    } finally {
      console.log(
        '📋 In finally block - versionCreated:',
        versionCreated,
        'hadError:',
        hadError,
      );
      this.cleanupAfterCreation();

      // Always close dialog if version was successfully created
      if (versionCreated) {
        console.log('🚪 Closing dialog...');
        this.dialogRef.close({
          project: updatedProject,
          response,
          majorVersion: majorVersionNum,
          minorVersion: minorVersionNum,
        });
        console.log('✅ Dialog close called');
      } else {
        console.log('⚠️ Dialog NOT closed - version was not created');
      }
    }
  }

  // ========================================
  // REFACTORED HELPER METHODS
  // ========================================

  private resetForm(): void {
    this.versionForm.reset();
    (Object as any)
      .values(this.versionForm.controls)
      .forEach((control: FormControl) => {
        control.setErrors(null);
        control.markAsUntouched();
        control.markAsPristine();
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ========================================
  // REFACTORED HELPER METHODS
  // ========================================

  /**
   * PHASE 1: Call backend API to create new version
   */
  private async callBackendCreateVersion(
    majorVersion: number,
    minorVersion: number,
  ): Promise<any> {
    console.log(
      `🚀 PHASE 1: Creating version ${majorVersion}.${minorVersion}...`,
    );

    const message =
      (this.versionForm.get('message')?.value as string | null)?.trim() || null;
    const versionData = { majorVersion, minorVersion, message };
    const response = await firstValueFrom(
      this.projectService.createProjectVersion(
        this.data.projectId,
        versionData,
      ),
    );

    console.log('✅ PHASE 1 Complete: Version created via API');
    return response;
  }

  /**
   * PHASE 3: Clear all caches and refresh AppState
   */
  private async clearCachesAndRefreshState(): Promise<void> {
    console.log('🗑️ PHASE 3: Clearing caches...');

    // Clear all caches (including fullVersionCache)
    this.projectService.invalidateProjectCache(this.data.projectId);
    this.notebookDefaultFilesService.clearDefaultFilesCache();
    this.cacheService.clearCacheDataCache();

    console.log('🔄 Refreshing AppState (force refresh)...');
    // refreshProjectData() will clear fullVersionCache and GET fresh data
    await this.appStateService.refreshProjectData(this.data.projectId, true);

    console.log('✅ PHASE 3 Complete: AppState refreshed with fresh data');
  }

  /**
   * Build mapping from workflow name to workflow ID
   */
  private buildWorkflowMapping(workflows: any[]): Map<string, number> {
    const mapping = new Map<string, number>();
    for (const workflow of workflows) {
      mapping.set(workflow.name, workflow.workflowId);
      console.log(
        `🔗 Mapped workflow "${workflow.name}": old HEAD workflow ID → new HEAD ${workflow.workflowId}`,
      );
    }
    return mapping;
  }

  /**
   * Show success toast and close dialog
   */
  private showSuccessAndClose(
    majorVersion: number,
    minorVersion: number,
    updatedProject: FullProject,
    response: any,
  ): void {
    this.toastr.success(
      `Version v${majorVersion}.${minorVersion} created`,
      'Success',
    );
    this.dialogRef.close({ project: updatedProject, response });
  }

  /**
   * Handle version creation error
   */
  private handleCreationError(error: any): void {
    console.error('❌ Version creation failed:', error);
    const errorMessage =
      error?.error?.message || error?.message || 'Failed to create version';
    this.toastr.error(errorMessage, 'Error');
  }

  /**
   * Cleanup flags after creation
   */
  private cleanupAfterCreation(): void {
    this.isLoading.set(false);
    this.isCreatingVersion = false;
    this.resetForm();
  }

  /**
   * PHASE 2: Fetch updated project data
   * Backend has already created frozen version and new HEAD with copied files
   */
  private async fetchUpdatedProjectData(): Promise<FullProject> {
    console.log('🔍 PHASE 2: Fetching updated project data...');

    const updatedProject = await firstValueFrom(
      this.projectService.getProjectFresh(this.data.projectId),
    );

    console.log('✅ PHASE 2 Complete:', {
      currentVersionId: updatedProject.currentVersionId,
      versionsCount: updatedProject.versions?.length || 0,
      note: 'Backend created frozen version and new HEAD with files automatically',
    });

    return updatedProject;
  }

  /**
   * PHASE 4: Send RELOAD_VERSIONS message to project-header
   * Updates dropdown with new versions
   */
  private notifyProjectHeaderOfNewVersion(
    updatedProject: FullProject,
    response: any,
    majorVersion: number,
    minorVersion: number,
  ): void {
    console.log('📢 PHASE 4: Sending version reload message...');

    // Use fresh project from AppState (refreshed in PHASE 3)
    const freshProject = this.appStateService.project();

    this.messageService.sendMessage(RELOAD_VERSIONS, {
      project: freshProject,
      response,
      createdVersionMajor: majorVersion,
      createdVersionMinor: minorVersion,
    });

    console.log('✅ PHASE 4 Complete');
  }

  /**
   * PHASE 5: Navigate to created frozen version
   */
  private async navigateToCreatedVersion(
    updatedProject: FullProject,
    majorVersion: number,
    minorVersion: number,
  ): Promise<void> {
    console.log('📍 PHASE 5: Navigating to created version...');

    const createdVersion = updatedProject.versions.find(
      (v) => v.majorVersion === majorVersion && v.minorVersion === minorVersion,
    );

    if (createdVersion) {
      LocalstorageHelper.setLastInteractedVersion(
        this.data.projectId,
        createdVersion.versionId,
      );

      // After version creation, the backend reuses the old HEAD's versionId
      // for the frozen version. The URL already has ?versionId=<old HEAD>,
      // so navigating to the frozen version is a same-URL no-op.
      // First navigate to the new HEAD (different versionId) to force a URL
      // change, then navigate to the frozen version. Both navigations trigger
      // the guard via runGuardsAndResolvers: 'paramsOrQueryParamsChange'.
      const currentVersionId = this.appStateService.versionId();
      if (currentVersionId === createdVersion.versionId) {
        const newHeadVersionId = this.appStateService.headVersionId();
        if (
          newHeadVersionId > 0 &&
          newHeadVersionId !== createdVersion.versionId
        ) {
          await this.appStateService.navigateToVersion(newHeadVersionId);
        }
      }

      await this.appStateService.navigateToVersion(createdVersion.versionId);
      console.log(
        '✅ PHASE 5 Complete: Navigated to version',
        createdVersion.versionId,
      );
    } else if (updatedProject.latestVersionId) {
      LocalstorageHelper.setLastInteractedVersion(
        this.data.projectId,
        updatedProject.latestVersionId,
      );
      await this.appStateService.navigateToVersion(
        updatedProject.latestVersionId,
      );
      console.log('✅ PHASE 5 Complete: Navigated to latestVersionId');
    }
  }

  /**
   * PHASE 0: Fetch all default files from current HEAD before it gets frozen
   */
  private async fetchDefaultFilesFromCurrentHead(
    currentHeadVersionId: number,
  ): Promise<
    Map<
      number,
      { workflowName: string; files: Array<{ file: any; content: Blob }> }
    >
  > {
    console.log(
      '📦 PHASE 0: Fetching default files from current HEAD before freezing...',
    );
    console.log(
      `🔍 Using HEAD version ID: ${currentHeadVersionId} (captured at method start)`,
    );

    const filesMap = new Map<
      number,
      { workflowName: string; files: Array<{ file: any; content: Blob }> }
    >();

    const currentHeadVersion = await firstValueFrom(
      this.versionService.getProjectVersion(
        this.data.projectId,
        currentHeadVersionId,
      ),
    );

    if (!currentHeadVersion?.workflows?.length) {
      console.warn('⚠️ Current HEAD has no workflows - no files to copy');
      return filesMap;
    }

    for (const workflow of currentHeadVersion.workflows) {
      try {
        const files = await this.fetchFilesForWorkflow(
          currentHeadVersionId,
          workflow.workflowId,
          workflow.name,
        );

        if (files.length > 0) {
          filesMap.set(workflow.workflowId, {
            workflowName: workflow.name,
            files,
          });
        }
      } catch (err) {
        console.error(
          `❌ Error fetching files for workflow ${workflow.workflowId}:`,
          err,
        );
      }
    }

    const totalFiles = Array.from(filesMap.values()).reduce(
      (sum, w) => sum + w.files.length,
      0,
    );
    console.log('✅ PHASE 0 Complete:', {
      workflowsWithFiles: filesMap.size,
      totalFiles,
    });

    return filesMap;
  }

  /**
   * Fetch all files with content for a specific workflow
   */
  private async fetchFilesForWorkflow(
    versionId: number,
    workflowId: number,
    workflowName: string,
  ): Promise<Array<{ file: any; content: Blob }>> {
    console.log(
      `🔍 Fetching default files for workflow "${workflowName}" (ID: ${workflowId})`,
    );

    const files = await firstValueFrom(
      this.notebookDefaultFilesService.getDefaultFilesFromVersion(
        this.data.projectId,
        versionId,
        workflowId,
      ),
    );

    if (!files?.length) {
      console.log(`ℹ️ No default files in workflow "${workflowName}"`);
      return [];
    }

    console.log(`📋 Found ${files.length} files in workflow "${workflowName}"`);

    const filesWithContent: Array<{ file: any; content: Blob }> = [];

    for (const file of files) {
      try {
        console.log(`📥 Downloading: ${file.name}`);
        const content = await firstValueFrom(
          this.notebookDefaultFilesService.getDefaultFileContentsFromVersion(
            this.data.projectId,
            versionId,
            workflowId,
            file.fileId.toString(),
            'blob',
          ),
        );
        filesWithContent.push({ file, content });
        console.log(`✅ Downloaded: ${file.name} (${content.size} bytes)`);
      } catch (err) {
        console.error(`❌ Failed to fetch content for ${file.name}:`, err);
      }
    }

    return filesWithContent;
  }

  /**
   * PHASE 3: Upload files to both frozen version and new HEAD
   */
  private async uploadFilesToBothVersions(
    filesMap: Map<
      number,
      { workflowName: string; files: Array<{ file: any; content: Blob }> }
    >,
    frozenVersionId: number,
    newHeadVersionId: number,
  ): Promise<void> {
    console.log('📋 PHASE 3: Uploading files to BOTH versions...');
    console.log('🔍 Version IDs:', { frozenVersionId, newHeadVersionId });

    const totalFrozen = await this.uploadToFrozenVersion(
      filesMap,
      frozenVersionId,
    );
    const totalNewHead = await this.uploadToNewHead(filesMap, newHeadVersionId);

    console.log(
      `✅ PHASE 3 Complete: Uploaded ${totalFrozen} files to frozen + ${totalNewHead} files to new HEAD`,
    );
  }

  /**
   * PHASE 3A: Upload files to frozen version
   */
  private async uploadToFrozenVersion(
    filesMap: Map<
      number,
      { workflowName: string; files: Array<{ file: any; content: Blob }> }
    >,
    frozenVersionId: number,
  ): Promise<number> {
    console.log('📋 PHASE 3A: Uploading files to FROZEN version...');

    if (filesMap.size === 0) {
      console.log('ℹ️ PHASE 3A: No files to upload (filesMap empty)');
      return 0;
    }

    let totalUploaded = 0;

    for (const [oldWorkflowId, { workflowName, files }] of filesMap) {
      console.log(
        `📋 Uploading ${files.length} files to frozen workflow "${workflowName}" (ID: ${oldWorkflowId})`,
      );

      for (const { file, content } of files) {
        try {
          const formData = new FormData();
          formData.append('name', file.name);
          formData.append('file', content, file.name);

          await firstValueFrom(
            this.notebookDefaultFilesService.createDefaultFileToVersion(
              this.data.projectId,
              frozenVersionId,
              oldWorkflowId,
              formData,
            ),
          );

          totalUploaded++;
          console.log(`✅ Uploaded to frozen: ${file.name}`);
        } catch (err) {
          console.error(`❌ Failed to upload ${file.name} to frozen:`, err);
        }
      }
    }

    console.log(`✅ PHASE 3A Complete: ${totalUploaded} files uploaded`);
    return totalUploaded;
  }

  /**
   * PHASE 3B: Upload files to new HEAD
   */
  private async uploadToNewHead(
    filesMap: Map<
      number,
      { workflowName: string; files: Array<{ file: any; content: Blob }> }
    >,
    newHeadVersionId: number,
  ): Promise<number> {
    console.log('📋 PHASE 3B: Uploading files to NEW HEAD...');

    const newHeadVersion = await firstValueFrom(
      this.versionService.getProjectVersion(
        this.data.projectId,
        newHeadVersionId,
      ),
    );

    if (!newHeadVersion?.workflows?.length) {
      console.warn('⚠️ New HEAD has no workflows');
      return 0;
    }

    const workflowMapping = this.buildWorkflowMapping(newHeadVersion.workflows);
    let totalUploaded = 0;

    for (const [oldWorkflowId, { workflowName, files }] of filesMap) {
      const newWorkflowId = workflowMapping.get(workflowName);

      if (!newWorkflowId) {
        console.warn(`⚠️ Workflow "${workflowName}" not found in new HEAD`);
        continue;
      }

      console.log(
        `📋 Uploading ${files.length} files to new HEAD "${workflowName}" (${oldWorkflowId}→${newWorkflowId})`,
      );

      for (const { file, content } of files) {
        try {
          const formData = new FormData();
          formData.append('name', file.name);
          formData.append('file', content, file.name);

          await firstValueFrom(
            this.notebookDefaultFilesService.createDefaultFile(
              this.data.projectId,
              newWorkflowId,
              formData,
              true,
            ),
          );

          totalUploaded++;
          console.log(`✅ Uploaded to new HEAD: ${file.name}`);
        } catch (err) {
          console.error(`❌ Failed to upload ${file.name} to new HEAD:`, err);
        }
      }
    }

    console.log(`✅ PHASE 3B Complete: ${totalUploaded} files uploaded`);
    return totalUploaded;
  }
}
